import { convert } from 'html-to-text';
import TurndownService from 'turndown';

const turndownService = new TurndownService({
  headingStyle: 'atx',
  bulletListMarker: '•'
});

export const exportMeetingMinutes = async (content, format, formData = {}) => {
  const timestamp = new Date().toISOString().split('T')[0];
  const meetingTitle = formData?.meetingTitle || 'Meeting-Minutes';
  const baseFilename = `${meetingTitle.replace(/[^a-zA-Z0-9]/g, '-')}-${timestamp}`;

  switch (format) {
    case 'html':
      return {
        content: convertToHTML(content, formData),
        filename: `${baseFilename}.html`,
        mimeType: 'text/html'
      };

    case 'markdown':
      return {
        content: content, // Already in markdown-like format
        filename: `${baseFilename}.md`,
        mimeType: 'text/markdown'
      };

    case 'text':
      return {
        content: convertToPlainText(content),
        filename: `${baseFilename}.txt`,
        mimeType: 'text/plain'
      };

    case 'docx':
      return {
        content: convertToWordFormat(content, formData),
        filename: `${baseFilename}.html`,
        mimeType: 'text/html'
      };

    default:
      throw new Error(`Unsupported export format: ${format}`);
  }
};

const convertToHTML = (content, formData) => {
  // Convert markdown-like content to HTML
  let html = content
    // Convert headers
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    // Convert bold text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    // Convert action items
    .replace(/^- \[ \] (.+)$/gm, '<div class="action-item pending"><input type="checkbox" disabled> $1</div>')
    .replace(/^- \[x\] (.+)$/gm, '<div class="action-item completed"><input type="checkbox" checked disabled> $1</div>')
    // Convert bullet points
    .replace(/^• (.+)$/gm, '<li>$1</li>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    // Convert line breaks
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br>');

  // Wrap bullet points in ul tags
  html = html.replace(/(<li>.*<\/li>)/gs, (match) => {
    return `<ul>${match}</ul>`;
  });

  // Wrap content in paragraphs
  html = `<p>${html}</p>`;

  // Clean up empty paragraphs and fix structure
  html = html
    .replace(/<p><\/p>/g, '')
    .replace(/<p>(<h[1-6]>)/g, '$1')
    .replace(/(<\/h[1-6]>)<\/p>/g, '$1')
    .replace(/<p>(<ul>)/g, '$1')
    .replace(/(<\/ul>)<\/p>/g, '$1')
    .replace(/<p>(<div)/g, '$1')
    .replace(/(<\/div>)<\/p>/g, '$1');

  const meetingTitle = formData?.meetingTitle || 'Meeting Minutes';
  const meetingDate = formData?.meetingDate ? new Date(formData.meetingDate).toLocaleDateString() : new Date().toLocaleDateString();

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${meetingTitle} - ${meetingDate}</title>
  <style>
    body {
      font-family: 'Arial', sans-serif;
      line-height: 1.6;
      max-width: 800px;
      margin: 0 auto;
      padding: 40px 20px;
      color: #333;
    }
    .header {
      text-align: center;
      border-bottom: 3px solid #8b5cf6;
      padding-bottom: 20px;
      margin-bottom: 30px;
    }
    .header h1 {
      color: #6d28d9;
      margin-bottom: 10px;
      font-size: 28px;
    }
    .meta-info {
      background: #f8fafc;
      padding: 20px;
      border-radius: 8px;
      margin-bottom: 30px;
      border-left: 4px solid #8b5cf6;
    }
    h1 {
      color: #6d28d9;
      border-bottom: 2px solid #e5e7eb;
      padding-bottom: 10px;
      font-size: 24px;
    }
    h2 {
      color: #7c3aed;
      margin-top: 30px;
      font-size: 20px;
    }
    h3 {
      color: #8b5cf6;
      margin-top: 20px;
      font-size: 16px;
    }
    ul {
      margin: 15px 0;
      padding-left: 25px;
    }
    li {
      margin: 8px 0;
      line-height: 1.5;
    }
    strong {
      color: #374151;
      font-weight: 600;
    }
    .action-item {
      background: #fef3c7;
      border-left: 4px solid #f59e0b;
      padding: 10px 15px;
      margin: 10px 0;
      border-radius: 4px;
    }
    .action-item.completed {
      background: #dcfce7;
      border-left-color: #22c55e;
      text-decoration: line-through;
      opacity: 0.8;
    }
    .action-item input {
      margin-right: 10px;
    }
    @media print {
      body {
        padding: 20px;
      }
      .action-item {
        break-inside: avoid;
      }
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>${meetingTitle}</h1>
    <p>Meeting Minutes</p>
  </div>
  
  <div class="meta-info">
    <strong>Date:</strong> ${meetingDate}<br>
    ${formData?.attendees ? `<strong>Attendees:</strong> ${formData.attendees}<br>` : ''}
    <strong>Generated:</strong> ${new Date().toLocaleString()}
  </div>
  
  ${html}
</body>
</html>`;
};

const convertToPlainText = (content) => {
  // Remove markdown formatting for plain text
  return content
    .replace(/^#{1,6} /gm, '') // Remove header markers
    .replace(/\*\*(.+?)\*\*/g, '$1') // Remove bold markers
    .replace(/^• /gm, '- ') // Convert bullet points
    .replace(/^- \[ \] /gm, '☐ ') // Convert unchecked action items
    .replace(/^- \[x\] /gm, '☑ ') // Convert checked action items
    .trim();
};

const convertToWordFormat = (content, formData) => {
  // For Word format, we'll generate HTML that can be easily converted
  return convertToHTML(content, formData);
};

export default {
  exportMeetingMinutes
};